// src/lib/api.ts
import { isNativePlatform } from "@/lib/runtime";

const RAW_API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL ?? "";

function isHttpUrl(value: string): boolean {
  return value.startsWith("http://") || value.startsWith("https://");
}

function normalizeConfiguredApiBase(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed || !isHttpUrl(trimmed)) {
    return null;
  }

  try {
    return new URL(trimmed).toString();
  } catch {
    return null;
  }
}

function readBrowserHttpBaseUrl(): string | null {
  if (typeof window === "undefined") return null;

  const candidates = [
    window.location.origin,
    window.location.href,
    typeof document !== "undefined" ? document.baseURI : "",
  ];

  for (const candidate of candidates) {
    const trimmed = candidate.trim();
    if (!trimmed || !isHttpUrl(trimmed)) {
      continue;
    }

    try {
      return new URL(trimmed).origin;
    } catch {
      // Ignore malformed runtime candidates and continue.
    }
  }

  return null;
}

function resolveAgainstBase(path: string, base: string): string {
  return new URL(path, base).toString();
}

function shouldUseSameOriginApi(path: string): boolean {
  if (typeof window === "undefined") return false;
  if (isNativePlatform()) return false;
  return path.startsWith("/") && !isHttpUrl(path);
}

function resolveConfiguredApiUrl(path: string): string | null {
  const configuredApiBase = normalizeConfiguredApiBase(RAW_API_BASE);
  if (!configuredApiBase) {
    return null;
  }

  try {
    return resolveAgainstBase(path, configuredApiBase);
  } catch {
    return null;
  }
}

/**
 * Build a full URL for API calls.
 * If you pass an absolute URL, it is returned as-is.
 */
export function apiUrl(path: string): string {
  if (isHttpUrl(path)) return path;
  if (shouldUseSameOriginApi(path)) return path;

  const configuredUrl = resolveConfiguredApiUrl(path);
  if (configuredUrl) {
    return configuredUrl;
  }

  if (path.startsWith("/")) {
    const browserBase = readBrowserHttpBaseUrl();
    if (browserBase) {
      return isNativePlatform() ? resolveAgainstBase(path, browserBase) : path;
    }
  }

  return path;
}

/**
 * Resolve an API path to either an absolute URL or a same-origin URL when possible.
 */
export function resolveApiUrl(path: string): string {
  const resolved = apiUrl(path);
  if (isHttpUrl(resolved)) return resolved;

  const browserBase = readBrowserHttpBaseUrl();
  if (browserBase) {
    return resolveAgainstBase(resolved, browserBase);
  }

  return resolved;
}

/**
 * Resolve an API path to an absolute http(s) URL.
 * Needed for native flows that open external browser windows.
 */
export function resolveAbsoluteApiUrl(path: string): string {
  if (isHttpUrl(path)) return path;

  const configuredUrl = resolveConfiguredApiUrl(path);
  if (configuredUrl) return configuredUrl;

  const browserBase = readBrowserHttpBaseUrl();
  if (browserBase) {
    return resolveAgainstBase(path, browserBase);
  }

  throw new Error(
    "Cannot resolve absolute API URL. Set NEXT_PUBLIC_API_BASE_URL to an absolute http(s) URL."
  );
}

/**
 * Standard JSON fetch helper with cookies included.
 */
export async function fetchJson<T>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const res = await fetch(apiUrl(path), {
    credentials: "include",
    ...init,
  });

  if (!res.ok) {
    throw new Error(`Request failed: ${res.status} ${res.statusText}`);
  }

  return res.json() as Promise<T>;
}
